import React, { useEffect, useState } from "react";
import SummaryApi from "../common";
import { useNavigate, useLocation } from "react-router-dom";
import displayINRCurrency from "../helpers/displayCurrency";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa6";
import {
  FaHistory,
  FaFileInvoice,
  FaTimes,
  FaEye,
  FaEdit,
} from "react-icons/fa";
import scrollTop from "../helpers/scrollTop";
const getTodayDate = () => new Date().toISOString().split("T")[0];
const StudentReports = () => {
  const [students, setStudents] = useState([]);
const [form, setForm] = useState({
  student_id: "",
  student_admission_number: "",
  full_name: "",
  father_name: "",
  mobile_number: "",
  email_id: "",
  course: "",
  year: "",
  semester: "",
  payment_method: "",
  payment_date: getTodayDate(),   // ← default to today
  other_charges: "",
  transaction_id: "",
  remarks: "",
  due_date: "",
  total_fee: "",
  paid_fee: "",
  balance_fee: ""
});
  const [editingIndex, setEditingIndex] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const studentsPerPage = 10;
  const navigate = useNavigate();
  const location = useLocation();

  // View Student Modal States
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);

  // Payment History Modal States
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchTodayStudents();
  }, []);

  // Handle edit from navigation state
  useEffect(() => {
    if (location.state?.isEditing && location.state?.editStudent) {
      const student = location.state.editStudent;
      handleEditStudent(student);
      // Clear the state to prevent re-triggering on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchTodayStudents = async () => {
    try {
      const response = await fetch(SummaryApi.getAllTodayStudents.url);
      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        setStudents(result.data);
      } else {
        console.error("Unexpected response:", result);
      }
    } catch (error) {
      console.error("Error fetching today's students:", error);
    }
  };

  // Fetch payment history for a specific student
  const fetchPaymentHistory = async (studentId) => {
    setLoadingHistory(true);
    try {
      const response = await fetch(SummaryApi.getpaymentallhistory.url, {
        method: SummaryApi.getpaymentallhistory.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ student_id: studentId }),
      });

      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        setPaymentHistory(result.data);
      } else {
        setPaymentHistory([]);
        toast.info(result.message || "No payment history found");
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
      toast.error("Failed to fetch payment history");
      setPaymentHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Open View Student Modal
  const handleViewStudent = (student) => {
    setViewStudent(student);
    setShowViewModal(true);
  };

  // Open Edit Student Mode
  const handleEditStudent = (student) => {
    setForm({
      student_id: student.student_id || "",
      student_admission_number: student.student_admission_number || "",
      full_name: student.full_name || "",
      father_name: student.father_name || "",
      mobile_number: student.mobile_number || "",
      email_id: student.email_id || "",
      course: student.course || "",
      year: student.year || "",
      semester: student.semester || "",
payment_method: "",
payment_date: getTodayDate(),   // ← default to today
other_charges: "",
      transaction_id: "",
      remarks: student.remarks || "",
      due_date: "",
      total_fee: "",
      paid_fee: "",
      balance_fee: ""
    });
    setEditingIndex(student.student_id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Open Payment History Modal
  const handleViewPaymentHistory = (student) => {
    setSelectedStudent(student);
    setShowPaymentHistory(true);
    fetchPaymentHistory(student.student_id);
  };

  // Handle Invoice with specific payment - passes payment_id to invoice
  const handleInvoiceWithPayment = (student, payment) => {
    try {
      navigate(`/invoice?student_id=${student.student_id}&payment_id=${payment.payment_id}`, {
        state: {
          student_id: student.student_id,
          payment_id: payment.payment_id,
          isHistory: true
        },
      });
    } catch (error) {
      console.error("Error opening invoice:", error);
      toast.error("Failed to open invoice");
    }
  };

  // Get the latest payment from payments array
  const getLatestPayment = (payments) => {
    if (!payments || payments.length === 0) return null;
    // Sort by created_date (newest first) and return the first one
    const sortedPayments = [...payments].sort((a, b) => 
      new Date(b.created_date) - new Date(a.created_date)
    );
    return sortedPayments[0];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const uppercaseFields = [
      "full_name",
      "transaction_id",
      "student_admission_number",
    ];
    const updatedValue = uppercaseFields.includes(name)
      ? value.toUpperCase()
      : value;

    const updatedForm = {
      ...form,
      [name]: updatedValue,
    };

    if (name === "total_fee" || name === "paid_fee") {
      const totalFee = parseFloat(name === "total_fee" ? updatedValue : form.total_fee) || 0;
      const paidFee = parseFloat(name === "paid_fee" ? updatedValue : form.paid_fee) || 0;
      updatedForm.balance_fee = totalFee - paidFee;
    }

    setForm(updatedForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    // Fee/Payment validations - only run in create mode (not edit mode)
    if (editingIndex === null) {
      if (form.payment_method !== "Cash" && form.transaction_id.trim() === "") {
        toast.error("Transaction ID is required for non-cash payments.");
        setIsSubmitting(false);
        return;
      }

      if (form.due_date) {
        const dueDate = new Date(form.due_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dueDate < today) {
          toast.error("Due date cannot be in the past.");
          setIsSubmitting(false);
          return;
        }
      }
    }

    try {
      let studentId = form.student_id;

      if (editingIndex !== null) {
        const studentApi = SummaryApi.updateStudent;
        const studentData = {
          student_id: form.student_id,
          student_admission_number: form.student_admission_number,
          full_name: form.full_name,
          father_name: form.father_name,
          mobile_number: form.mobile_number,
          email_id: form.email_id,
          course: form.course,
          year: form.year,
          semester: form.semester,
          remarks: form.remarks
        };

        const studentResponse = await fetch(studentApi.url, {
          method: studentApi.method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(studentData),
        });

        const studentResult = await studentResponse.json();

        if (!studentResult.status) {
          toast.error(studentResult.message || "Error updating student");
          setIsSubmitting(false);
          return;
        }

        studentId = form.student_id;
        toast.success("Student updated successfully!");
      } else {
        const studentApi = SummaryApi.createStudent;
        const studentData = {
          student_admission_number: form.student_admission_number,
          full_name: form.full_name,
          father_name: form.father_name,
          mobile_number: form.mobile_number,
          email_id: form.email_id,
          course: form.course,
          year: form.year,
          semester: form.semester,
          remarks: form.remarks
        };

        const studentResponse = await fetch(studentApi.url, {
          method: studentApi.method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(studentData),
        });

        const studentResult = await studentResponse.json();

        if (!studentResult.status) {
          toast.error(studentResult.message || "Error creating student");
          setIsSubmitting(false);
          return;
        }

        studentId = studentResult.student_id;

        if (!studentId) {
          toast.error("Student ID not received from server");
          setIsSubmitting(false);
          return;
        }

        toast.success(studentResult.message || "Student created successfully.");
        setForm(prev => ({ ...prev, student_id: studentId }));
      }

      // Only create payment in create mode, not in edit mode
      if (editingIndex === null) {
        const totalFee = parseFloat(form.total_fee) || 0;
        const paidFee = parseFloat(form.paid_fee) || 0;
        const balanceFee = totalFee - paidFee;

const paymentData = {
  student_id: studentId,
  total_fee: totalFee,
  paid_fee: paidFee,
  balance_fee: balanceFee,
  payment_date: form.payment_date,   // ← add this line
  due_date: form.due_date,
  payment_method: form.payment_method,
  transaction_id: form.transaction_id,
  remarks: form.remarks
};

        if (!SummaryApi.paymentCreate) {
          toast.error("Payment API configuration missing");
          setIsSubmitting(false);
          return;
        }

        const paymentResponse = await fetch(SummaryApi.paymentCreate.url, {
          method: SummaryApi.paymentCreate.method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(paymentData),
        });

        const paymentResult = await paymentResponse.json();
        
        // 🔍 LOG THE RAW RESPONSE TO SEE THE ACTUAL SHAPE
        console.log("RAW paymentResult:", JSON.stringify(paymentResult, null, 2));

        if (!paymentResult.status) {
          toast.error(paymentResult.message || "Error creating payment");
          setIsSubmitting(false);
          return;
        }

        await fetchTodayStudents();
        const studentData = {
          ...form,
          student_id: studentId,
          balance_fee: balanceFee
        };

        // 🎯 FIX: Robust payment data extraction - check ALL possible locations
        // Try paymentResult.data, then paymentResult.payment, then paymentResult itself
        const payload = paymentResult.data || paymentResult.payment || paymentResult;
        
        // Build payment data with priority: server response > request data
        const paymentDataToSend = {
          ...paymentData,  // Start with what we sent (fallback)
          ...payload,      // Overwrite with server response
          // Explicitly grab payment_id from any possible location
          payment_id: payload.payment_id || payload.id || payload.paymentId || "",
          // Explicitly grab invoice_number from any possible location
          invoice_number: payload.invoice_number || payload.invoiceNumber || "",
        };

        // Log the final data to verify
        console.log("✅ Final payment data sent to invoice:", paymentDataToSend);
        console.log("✅ payment_id:", paymentDataToSend.payment_id);
        console.log("✅ invoice_number:", paymentDataToSend.invoice_number);

        // If payment_id is still empty, generate a fallback
        if (!paymentDataToSend.payment_id) {
          console.warn("⚠️ No payment_id found in response - generating fallback");
          // Try to fetch the payment by student_id to get the latest
          try {
            const historyResponse = await fetch(SummaryApi.getpaymentallhistory.url, {
              method: SummaryApi.getpaymentallhistory.method,
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ student_id: studentId }),
            });
            const historyResult = await historyResponse.json();
            if (historyResult.status && historyResult.data && historyResult.data.length > 0) {
              const latestPayment = historyResult.data[historyResult.data.length - 1];
              paymentDataToSend.payment_id = latestPayment.payment_id;
              paymentDataToSend.invoice_number = latestPayment.invoice_number || paymentDataToSend.invoice_number;
              console.log("✅ Retrieved payment from history:", latestPayment);
            }
          } catch (historyError) {
            console.error("Error fetching payment history:", historyError);
          }
        }

        // If still no payment_id, use a timestamp as fallback
        if (!paymentDataToSend.payment_id) {
          const fallbackId = Date.now();
          paymentDataToSend.payment_id = fallbackId;
          paymentDataToSend.invoice_number = paymentDataToSend.invoice_number || `INV-${fallbackId}`;
          console.warn("⚠️ Using fallback payment_id:", fallbackId);
        }

        navigate(`/invoice?student_id=${studentId}&payment_id=${paymentDataToSend.payment_id}`, {
          state: {
            student: studentData,
            payment: paymentDataToSend,
            isEditing: false
          },
        });
      } else {
        // Edit mode - just refresh the student list
        await fetchTodayStudents();
        // Reset editing state after update
        setEditingIndex(null);
setForm({
  student_id: "",
  student_admission_number: "",
  full_name: "",
  father_name: "",
  mobile_number: "",
  email_id: "",
  course: "",
  year: "",
  semester: "",
  payment_method: "",
  payment_date: getTodayDate(),   // ← default to today
  other_charges: "",
  transaction_id: "",
  remarks: "",
  due_date: "",
  total_fee: "",
  paid_fee: "",
  balance_fee: ""
});
      }

      setIsSubmitting(false);

    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.message || "Submission error. Please try again.");
      setIsSubmitting(false);
    }
  };

  const filteredStudents = students.filter((student) => {
    const searchValue = search.toLowerCase();
    return (
      student.student_admission_number?.toLowerCase().includes(searchValue) ||
      student.mobile_number?.includes(searchValue)
    );
  });

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const handleDownloadExcel = () => {
    const data = filteredStudents.map(
      (
        {
          student_admission_number,
          full_name,
          father_name,
          mobile_number,
          email_id,
          course,
          year,
          semester,
          remarks,
          payments
        },
        index
      ) => {
        const latestPayment = getLatestPayment(payments);
        return {
          "S.No": index + 1,
          "Admission Number": student_admission_number,
          "Student Name": full_name,
          "Father Name": father_name,
          "Mobile": mobile_number,
          "Email": email_id,
          "Course": course,
          "Year": year,
          "Semester": semester,
          "Total Fee": latestPayment ? latestPayment.total_fee : "N/A",
          "Paid Fee": latestPayment ? latestPayment.paid_fee : "N/A",
          "Balance Fee": latestPayment ? latestPayment.balance_fee : "N/A",
          "Payment Method": latestPayment ? latestPayment.payment_method : "N/A",
          "Transaction ID": latestPayment ? latestPayment.transaction_id : "N/A",
          "Invoice Number": latestPayment ? latestPayment.invoice_number : "N/A",
          "Due Date": latestPayment ? latestPayment.due_date : "N/A",
          "Remarks": remarks || "N/A",
        };
      }
    );

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "StudentReports.xlsx");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 15);
    return date.toISOString().split("T")[0];
  };

  const getMaxDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const getMinDueDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setEditingIndex(null);
setForm({
  student_id: "",
  student_admission_number: "",
  full_name: "",
  father_name: "",
  mobile_number: "",
  email_id: "",
  course: "",
  year: "",
  semester: "",
  payment_method: "",
  payment_date: getTodayDate(),   // ← default to today
  other_charges: "",
  transaction_id: "",
  remarks: "",
  due_date: "",
  total_fee: "",
  paid_fee: "",
  balance_fee: ""
});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="container mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">
        {editingIndex !== null ? "Edit Student" : "Student Create"}
        {editingIndex !== null && (
          <button
            onClick={handleCancelEdit}
            className="ml-4 bg-red-500 hover:bg-red-600 text-white px-4 py-1 rounded text-sm"
          >
            Cancel Edit
          </button>
        )}
      </h2>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-6 mb-6 bg-white p-6 shadow rounded"
      >
        {/* Student Details Section */}
        <div>
          <h3 className="text-lg font-semibold mb-6 border-b pb-1">
            Student Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="student_admission_number" className="block font-medium mb-1">
                Student Admission Number
              </label>
              <input
                id="student_admission_number"
                type="text"
                name="student_admission_number"
                value={form.student_admission_number}
                onChange={handleChange}
                readOnly={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
              />
            </div>

            <div>
              <label htmlFor="full_name" className="block font-medium mb-1">
                Full Name<span className="text-red-500">*</span>
              </label>
              <input
                id="full_name"
                type="text"
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              />
            </div>
            <div>
              <label htmlFor="father_name" className="block font-medium mb-1">
                Father Name<span className="text-red-500">*</span>
              </label>
              <input
                id="father_name"
                type="text"
                name="father_name"
                value={form.father_name}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              />
            </div>

            <div>
              <label
                htmlFor="mobile_number"
                className="block font-medium mb-1 "
              >
                Mobile Number<span className="text-red-500">*</span>
              </label>
              <input
                id="mobile_number"
                type="text"
                name="mobile_number"
                value={form.mobile_number}
                onChange={handleChange}
                readOnly={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                maxLength={10}
                required
              />
            </div>
            <div>
              <label htmlFor="course" className="block font-medium mb-1">
                Course<span className="text-red-500">*</span>
              </label>
              <select
                id="course"
                name="course"
                value={form.course}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              >
                <option value="">Select a course</option>
                <option value="CSE">CSE</option>
                <option value="AI & ML">AI & ML</option>
                <option value="Aeronautical Engineering">
                  Aeronautical Engineering
                </option>
                <option value="ECE">ECE</option>
                <option value="BBA LLB">BBA LLB</option>
                <option value="BA LLB">BA LLB</option>
                <option value="BBA">BBA</option>
                <option value="BSc Food Science & Tech">
                  BSc Food Science & Tech
                </option>
              </select>
            </div>

            <div>
              <label htmlFor="year" className="block font-medium mb-1">
                Year<span className="text-red-500">*</span>
              </label>
              <select
                id="year"
                name="year"
                value={form.year}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              >
                <option value="">Select a Year</option>
                <option value="I">1</option>
                <option value="II">2</option>
                <option value="III">3</option>
                <option value="IV">4</option>
              </select>
            </div>
            <div>
              <label htmlFor="semester" className="block font-medium mb-1">
                Semester<span className="text-red-500">*</span>
              </label>
              <select
                id="semester"
                name="semester"
                value={form.semester}
                onChange={handleChange}
                className="border p-2 rounded w-full"
                required
              >
                <option value="">Select a Semester</option>
                <option value="I">1</option>
                <option value="II">2</option>
              </select>
            </div>
            <div>
              <label htmlFor="email_id" className="block font-medium mb-1">
                Email
              </label>
              <input
                id="email_id"
                type="email"
                name="email_id"
                value={form.email_id}
                onChange={handleChange}
                className="border p-2 rounded w-full"
              />
            </div>
          </div>
        </div>

        {/* Fee Details Section */}
        <div>
          <h3 className="text-lg font-semibold mb-6 border-b pb-1">
            Fee Information
            {editingIndex !== null && (
              <span className="text-sm text-red-500 ml-2">(Read-only in edit mode)</span>
            )}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="total_fee" className="block font-medium mb-1">
                Total Fee<span className="text-red-500">*</span>
              </label>
              <input
                id="total_fee"
                type="number"
                name="total_fee"
                value={form.total_fee}
                onChange={handleChange}
                disabled={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
                min={0}
              />
            </div>

            <div>
              <label htmlFor="paid_fee" className="block font-medium mb-1">
                Paid Fee<span className="text-red-500">*</span>
              </label>
              <input
                id="paid_fee"
                type="number"
                name="paid_fee"
                value={form.paid_fee}
                onChange={handleChange}
                disabled={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
                min={0}
              />
            </div>

            <div>
              <label htmlFor="balance_fee" className="block font-medium mb-1">
                Balance Fee
              </label>
              <input
                id="balance_fee"
                type="number"
                name="balance_fee"
                value={form.balance_fee}
                className="border p-2 rounded w-full bg-yellow-100 text-red-700 font-bold"
                disabled
              />
            </div>

            <div>
              <label htmlFor="other_charges" className="block font-medium mb-1">
                Other Charges
              </label>
              <input
                id="other_charges"
                type="number"
                name="other_charges"
                value={form.other_charges}
                onChange={handleChange}
                disabled={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                min={0}
              />
            </div>

            <div>
              <label htmlFor="due_date" className="block font-medium mb-1">
                Due Date<span className="text-red-500">*</span>
              </label>
              <input
                id="due_date"
                type="date"
                name="due_date"
                value={form.due_date}
                onChange={handleChange}
                disabled={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
                min={getMinDueDate()}
              />
            </div>
          </div>
        </div>

        {/* Transaction Details Section */}
        <div>
          <h3 className="text-lg font-semibold mb-6 border-b pb-1">
            Transaction Information
            {editingIndex !== null && (
              <span className="text-sm text-red-500 ml-2">(Read-only in edit mode)</span>
            )}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <label
                htmlFor="payment_method"
                className="block font-medium mb-1"
              >
                Payment Method<span className="text-red-500">*</span>
              </label>
              <select
                id="payment_method"
                name="payment_method"
                value={form.payment_method}
                onChange={handleChange}
                disabled={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
              >
                <option value="">Select Payment Method</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
            </div>

            <div>
              <label className="block mb-1 font-medium">
                Payment Date<span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="payment_date"
                value={form.payment_date}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const today = new Date();

                  selectedDate.setMinutes(
                    selectedDate.getMinutes() + selectedDate.getTimezoneOffset()
                  );
                  today.setHours(0, 0, 0, 0);

                  const minDate = new Date();
                  minDate.setDate(minDate.getDate() - 15);
                  minDate.setHours(0, 0, 0, 0);

                  if (selectedDate > today || selectedDate < minDate) {
                    toast.error(
                      "Payment date must be today or within the last 15 days."
                    );
                    return;
                  }
                  handleChange(e);
                }}
                disabled={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
                min={getMinDate()}
                max={getMaxDate()}
              />
            </div>

            <div>
              <label
                htmlFor="transaction_id"
                className="block font-medium mb-1"
              >
                Transaction ID
              </label>
              <input
                id="transaction_id"
                type="text"
                name="transaction_id"
                value={form.transaction_id}
                onChange={handleChange}
                disabled={editingIndex !== null}
                className={`border p-2 rounded w-full ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
              />
            </div>

            <div className="md:col-span-3">
              <label htmlFor="remarks" className="block font-medium mb-1">
                Remarks
              </label>
              <textarea
                id="remarks"
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
                disabled={editingIndex !== null}
                className={`border p-2 rounded w-full min-h-[60px] ${
                  editingIndex !== null ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                placeholder="Enter any additional notes here..."
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-right">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`bg-blue-600 text-white py-2 px-6 rounded transition ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
            }`}
          >
            {isSubmitting ? "Submitting..." : (editingIndex !== null ? "Update Student" : "Submit")}
          </button>
        </div>
      </form>

      <h3 className="text-2xl font-semibold mb-4">Today's Student Records</h3>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-4 w-full">
          <div className="w-full sm:w-96">
            <input
              type="text"
              placeholder="Search by Student Admission Number or Mobile Number"
              className="border p-2 rounded w-full"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <button
          onClick={handleDownloadExcel}
          className="bg-emerald-600 hover:bg-emerald-700 w-48 text-white py-2 px-4 rounded flex justify-center items-center gap-1"
        >
          <FaFileExcel /> Download Excel
        </button>
      </div>

      <div className="overflow-x-auto">
        <div className="h-[600px] overflow-y-auto">
          <table className="min-w-full bg-white shadow rounded text-sm border">
            <thead className="bg-gray-100 text-left">
              <tr>
                {[
                  "S NO",
                  "Admission Number",
                  "Student Name",
                  "Father Name",
                  "Mobile",
                  "Email",
                  "Course",
                  "Year",
                  "Semester",
                  "Total Fee",
                  "Paid Fee",
                  "Balance Fee",
                  "Payment Method",
                    "Payment Date",  
                  "Transaction ID",
                  "Actions",
                  "History",
                ].map((head, idx) => (
                  <th
                    key={idx}
                    className="px-4 py-2 border-b whitespace-nowrap"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length === 0 ? (
                <tr>
       <td colSpan="18" className="text-center py-4 text-gray-500">
                    No students found today.
                  </td>
                </tr>
              ) : (
                currentStudents.map((student, index) => {
                  const latestPayment = getLatestPayment(student.payments);
                  return (
                    <tr
                      key={student.student_id || index}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-2 border-b">
                        {indexOfFirstStudent + index + 1}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[150px] truncate">
                        {student.student_admission_number || "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[180px] truncate font-medium">
                        {student.full_name}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[180px] truncate">
                        {student.father_name}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[120px] truncate">
                        {student.mobile_number}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[200px] truncate">
                        {student.email_id || "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[150px] truncate">
                        {student.course}
                      </td>
                      <td className="px-4 py-2 border-b">
                        {student.year}
                      </td>
                      <td className="px-4 py-2 border-b">
                        {student.semester}
                      </td>
                      <td className="px-4 py-2 border-b">
                        {latestPayment ? displayINRCurrency(latestPayment.total_fee || 0) : "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b text-green-600">
                        {latestPayment ? displayINRCurrency(latestPayment.paid_fee || 0) : "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b text-red-600 font-bold">
                        {latestPayment ? displayINRCurrency(latestPayment.balance_fee || 0) : "N/A"}
                      </td>
  <td className="px-4 py-2 border-b">
  {latestPayment ? latestPayment.payment_method : "N/A"}
</td>
<td className="px-4 py-2 border-b">
  {latestPayment
    ? (latestPayment.payment_date
        ? formatDate(latestPayment.payment_date)
        : (latestPayment.created_date ? formatDate(latestPayment.created_date) : "N/A"))
    : "N/A"}
</td>
<td className="px-4 py-2 border-b">
  {latestPayment ? latestPayment.transaction_id || "N/A" : "N/A"}
</td>
                      <td className="px-4 py-2 border-b">
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={() => handleViewStudent(student)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1"
                            title="View Details"
                          >
                            <FaEye /> View
                          </button>
                          <button
                            onClick={() => handleEditStudent(student)}
                            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1"
                            title="Edit Student"
                          >
                            <FaEdit /> Edit
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-2 border-b">
                        <button
                          onClick={() => handleViewPaymentHistory(student)}
                          className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1"
                          title="Payment History"
                        >
                          <FaHistory /> History
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="relative">
          {totalPages > 1 ? (
            <div className="flex justify-center items-center gap-2 flex-wrap relative">
              <button
                className="px-3 py-1 mt-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className=" top-full mt-2 text-base font-semibold">
                Page {currentPage} of {totalPages}
              </div>
              <button
                className="px-3 py-1 mt-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>

              <div className="absolute right-20 text-base font-semibold">
                Total Records:{" "}
                <span className="ml-1">{filteredStudents.length}</span>
              </div>
            </div>
          ) : (
            <div className="flex justify-end text-base font-semibold">
              Total Records:{" "}
              <span className="ml-1">{filteredStudents.length}</span>
            </div>
          )}
        </div>
      </div>

      {/* View Student Modal */}
      {showViewModal && viewStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden text-xs mx-auto my-auto">
            <div className="flex justify-between items-center p-3 border-b sticky top-0 bg-white">
              <h2 className="text-base font-bold">
                Student Details - {viewStudent.full_name}
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700 text-base"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-3">
                  <h3 className="text-sm font-semibold mb-2 text-blue-600 border-b pb-1">
                    Personal Information
                  </h3>
                  <div className="space-y-2">

                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Admission Number</label>
                      <p className="text-xs font-semibold">{viewStudent.student_admission_number}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Full Name</label>
                      <p className="text-xs font-semibold">{viewStudent.full_name}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Father Name</label>
                      <p className="text-xs font-semibold">{viewStudent.father_name}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Mobile Number</label>
                      <p className="text-xs font-semibold">{viewStudent.mobile_number}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Email</label>
                      <p className="text-xs font-semibold">{viewStudent.email_id || "N/A"}</p>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-3">
                  <h3 className="text-sm font-semibold mb-2 text-green-600 border-b pb-1">
                    Academic Information
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Course</label>
                      <p className="text-xs font-semibold">{viewStudent.course}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Year</label>
                      <p className="text-xs font-semibold">{viewStudent.year}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Semester</label>
                      <p className="text-xs font-semibold">{viewStudent.semester}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Remarks</label>
                      <p className="text-xs font-semibold">{viewStudent.remarks || "N/A"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end p-3 border-t sticky bottom-0 bg-white">
              <button
                onClick={() => setShowViewModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showPaymentHistory && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
              <h2 className="text-2xl font-bold">
                Payment History - {selectedStudent.full_name}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({selectedStudent.student_admission_number})
                </span>
              </h2>
              <button
                onClick={() => setShowPaymentHistory(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {loadingHistory ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-500">Loading payment history...</p>
                </div>
              ) : paymentHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No payment history found for this student.
                </div>
              ) : (
                <table className="min-w-full bg-white border">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 border text-left">#</th>
   <th className="px-4 py-2 border text-left">Invoice No</th>
<th className="px-4 py-2 border text-left">Payment Date</th>
<th className="px-4 py-2 border text-left">Payment Method</th>
                      <th className="px-4 py-2 border text-left">Total Fee</th>
                      <th className="px-4 py-2 border text-left">Paid Fee</th>
                      <th className="px-4 py-2 border text-left">Balance</th>
                      <th className="px-4 py-2 border text-left">Due Date</th>
                      <th className="px-4 py-2 border text-left">Transaction ID</th>
                      <th className="px-4 py-2 border text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((payment, idx) => (
                      <tr key={payment.payment_id || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border">{idx + 1}</td>
   <td className="px-4 py-2 border font-semibold">
  {payment.invoice_number || "N/A"}
</td>
<td className="px-4 py-2 border">
  {payment.payment_date ? formatDate(payment.payment_date) : (payment.created_date ? formatDate(payment.created_date) : "N/A")}
</td>
<td className="px-4 py-2 border">{payment.payment_method}</td>
                        <td className="px-4 py-2 border">{displayINRCurrency(payment.total_fee || 0)}</td>
                        <td className="px-4 py-2 border text-green-600">
                          {displayINRCurrency(payment.paid_fee || 0)}
                        </td>
                        <td className="px-4 py-2 border text-red-600 font-bold">
                          {displayINRCurrency(payment.balance_fee || 0)}
                        </td>
                        <td className="px-4 py-2 border">
                          {payment.due_date ? formatDate(payment.due_date) : "N/A"}
                        </td>
                        <td className="px-4 py-2 border">{payment.transaction_id || "N/A"}</td>
                        <td className="px-4 py-2 border">
                          <button
                            onClick={() => handleInvoiceWithPayment(selectedStudent, payment)}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1"
                          >
                            <FaFileInvoice /> Invoice
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex justify-end p-4 border-t sticky bottom-0 bg-white">
              <button
                onClick={() => setShowPaymentHistory(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentReports;